import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Calendar, Settings, Info } from 'lucide-react';

interface Report {
  id: string;
  name: string;
  description: string;
  category: string;
  deliveryOptions: string[];
  deliveryMethods: string[];
}

interface CreateReportModalProps {
  report: Report;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateReportModal({ report, open, onOpenChange }: CreateReportModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl">Create Report</DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Report Info */}
          <div className="space-y-2 p-4 bg-muted/30 rounded-lg">
            <h3 className="font-semibold">{report.name}</h3>
            <p className="text-sm text-muted-foreground">{report.description}</p>
          </div>

          {/* Delivery Options */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              Delivery Options
            </Label>
            <Select defaultValue="run-now">
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {report.deliveryOptions.map(option => (
                  <SelectItem key={option} value={option.toLowerCase().replace(' ', '-')}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Delivery Methods */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-muted-foreground" />
              Delivery Methods
              <Info className="h-3 w-3 text-muted-foreground" />
            </Label>
            <Select defaultValue="download">
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {report.deliveryMethods.map(method => (
                  <SelectItem key={method} value={method.toLowerCase()}>
                    {method}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // Handle report creation
                console.log('Creating report:', report.name);
                onOpenChange(false);
              }}
            >
              Create Report
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

